import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, Navigate } from "react-router";
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  Check,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

import { CenteredLoader, PageIntro, Skeleton } from "@/components/common";
import { MasonryGrid } from "@/components/common/masonry-grid";
import { ApprovalPostCard } from "@/components/campaign/approval-post-card";
import { GeneratedPostVisualSpinner } from "@/components/campaign/generated-post-review-card";
import { ScheduledPostPreviewOverlay } from "@/components/campaign/scheduled-post-preview-overlay";
import { CalendarPostGrid } from "@/components/calendar/calendar-post-grid";
import { buildBrandPath, getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import {
  addCalendarDays,
  getCalendarRange,
  getCalendarWindowDays,
  getCalendarWindowStart,
  getStandaloneCalendarGroupLabel,
  groupCalendarPostsByDay,
  isCalendarPostUnapproved,
  type CalendarPostLike,
} from "@/lib/calendar-posts";
import { hasActiveInstagramPublishNow } from "@/lib/instagram-publication";
import { formatInstagramProfileLabel } from "@/lib/social-post-chrome";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { Badge, Button } from "@runcheetah/ui";
import {
  formatReviewPostGroupDateRange,
  getPendingReviewCampaigns,
  groupReviewPostsByOwner,
  isActivelyGeneratingReviewPost,
  shouldShowInstagramPublishingSetupBanner,
} from "./review-state";

type ApprovalView = "campaign" | "calendar";

const APPROVALS_GENERATION_REFETCH_INTERVAL_MS = 2_000;

const dateRangeFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});

const scheduledToastFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

export default function ApprovalsPage() {
  const intl = useIntl();
  const { brand, brandQuery } = useRouteBrand();
  const utils = trpc.useUtils();
  const [view, setView] = useState<ApprovalView>("campaign");
  const [calendarWindowStart, setCalendarWindowStart] = useState(() =>
    getCalendarWindowStart(new Date()),
  );

  const calendarDays = useMemo(
    () => getCalendarWindowDays(calendarWindowStart),
    [calendarWindowStart],
  );
  const calendarRange = useMemo(() => getCalendarRange(calendarWindowStart), [calendarWindowStart]);

  const postsQuery = trpc.calendar.list.useQuery(
    {
      brandId: brand?.id ?? "",
      approvalStatuses: ["pending", "approved"],
      postStatuses: ["generated", "generating", "scheduled"],
    },
    {
      enabled: Boolean(brand?.id),
      refetchOnWindowFocus: true,
      refetchInterval: (query) =>
        (query.state.data ?? []).some(isActivelyGeneratingReviewPost) ||
        hasActiveInstagramPublishNow(query.state.data)
          ? APPROVALS_GENERATION_REFETCH_INTERVAL_MS
          : false,
    },
  );
  const calendarPostsQuery = trpc.calendar.list.useQuery(
    {
      brandId: brand?.id ?? "",
      start: calendarRange.start,
      end: calendarRange.end,
      approvalStatuses: ["pending", "approved"],
    },
    {
      enabled: Boolean(brand?.id),
      refetchOnWindowFocus: true,
      refetchInterval: (query) =>
        hasActiveInstagramPublishNow(query.state.data)
          ? APPROVALS_GENERATION_REFETCH_INTERVAL_MS
          : false,
    },
  );
  const campaignsQuery = trpc.campaign.list.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), refetchOnWindowFocus: true },
  );
  const instagramConnectionQuery = trpc.socialConnection.getInstagram.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), refetchOnWindowFocus: true, staleTime: 60_000 },
  );
  const logosQuery = trpc.brand.listLogos.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: 0 },
  );
  const logoAssets = useMemo(
    () =>
      (logosQuery.data?.logos ?? []).map((logo) => ({
        objectKey: logo.objectKey,
        signedUrl: logo.signedUrl,
        isPrimary: logo.isPrimary,
      })),
    [logosQuery.data?.logos],
  );
  const pendingCampaigns = useMemo(
    () => getPendingReviewCampaigns(campaignsQuery.data ?? []),
    [campaignsQuery.data],
  );

  const approveMutation = trpc.calendar.approve.useMutation({
    onSuccess: () => {
      void utils.calendar.list.invalidate();
    },
  });
  const rejectMutation = trpc.calendar.reject.useMutation({
    onSuccess: () => {
      void utils.calendar.list.invalidate();
    },
  });
  const publishCampaignMutation = trpc.campaign.publishNowInstagram.useMutation();
  const publishStandaloneMutation = trpc.standaloneAssetBatch.publishNowInstagram.useMutation();

  const [approvingIds, setApprovingIds] = useState<Set<string>>(new Set());
  const [rejectingIds, setRejectingIds] = useState<Set<string>>(new Set());
  const [publishingIds, setPublishingIds] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const reviewPostGroupLabels = useMemo(
    () => ({
      standaloneTitle: intl.formatMessage({
        id: "approvals.oneOffPosts",
        defaultMessage: "One-off posts",
      }),
      standaloneDescription: intl.formatMessage({
        id: "approvals.oneOffPostsDescription",
        defaultMessage: "Posts created outside campaigns.",
      }),
      campaignDescription: intl.formatMessage({
        id: "approvals.generatedCampaignAssetsDescription",
        defaultMessage: "Generated assets from this campaign.",
      }),
    }),
    [intl],
  );

  if (brandQuery.isPending) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "approvals.loading",
          defaultMessage: "Loading review…",
        })}
      />
    );
  }
  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  const posts = (postsQuery.data ?? []).filter(
    (post) => post.approvalStatus !== "approved" || post.publication?.status !== "published",
  );
  const profileLabel = formatInstagramProfileLabel(brand.sourceDomain);
  const showPublishingSetupBanner = shouldShowInstagramPublishingSetupBanner(
    instagramConnectionQuery.data,
  );
  const generatedGroups = groupReviewPostsByOwner(posts, reviewPostGroupLabels, undefined, {
    includeApproved: true,
  });
  const activelyGeneratingPosts = posts.filter(isActivelyGeneratingReviewPost);
  const generatingGroups = groupReviewPostsByOwner(
    activelyGeneratingPosts,
    reviewPostGroupLabels,
    new Set(["scheduled", "generating"]),
  );
  const reviewGroups = generatedGroups.map((group) => ({
    ...group,
    generatingPosts:
      generatingGroups.find((generatingGroup) => generatingGroup.key === group.key)?.posts ?? [],
  }));
  for (const generatingGroup of generatingGroups) {
    if (generatedGroups.some((group) => group.key === generatingGroup.key)) {
      continue;
    }

    reviewGroups.push({
      ...generatingGroup,
      posts: [],
      generatingPosts: generatingGroup.posts,
    });
  }
  const visibleReviewPosts = reviewGroups.flatMap((group) =>
    collapsed.has(group.key) ? [] : group.posts,
  );
  const calendarPosts = (calendarPostsQuery.data ?? []).filter(
    (post) => post.approvalStatus !== "approved" || post.publication?.status !== "published",
  );
  const groupedCalendarDays = groupCalendarPostsByDay(calendarDays, calendarPosts, {
    standaloneLabel: getStandaloneCalendarGroupLabel(intl),
  });
  const orderedCalendarPosts = groupedCalendarDays.flatMap((day) =>
    day.groups.flatMap((group) => group.posts),
  );
  const visiblePosts = view === "calendar" ? orderedCalendarPosts : visibleReviewPosts;
  const selectedPost = selectedPostId
    ? (visiblePosts.find((post) => post.id === selectedPostId) ?? null)
    : null;
  const selectedPostIndex = selectedPost
    ? visiblePosts.findIndex((post) => post.id === selectedPost.id)
    : -1;
  const previousPost = selectedPostIndex > 0 ? visiblePosts[selectedPostIndex - 1] : null;
  const nextPost =
    selectedPostIndex >= 0 && selectedPostIndex < visiblePosts.length - 1
      ? visiblePosts[selectedPostIndex + 1]
      : null;
  const calendarPendingCount = calendarPosts.filter(isCalendarPostUnapproved).length;
  const calendarWindowEnd = addCalendarDays(calendarWindowStart, 6);
  const calendarTitleFormatter = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  });
  const approvePost = (brandId: string, postId: string) => {
    const post =
      posts.find((item) => item.id === postId) ?? calendarPosts.find((item) => item.id === postId);
    setApprovingIds((prev) => new Set(prev).add(postId));
    approveMutation.mutate(
      { brandId, scheduledPostId: postId },
      {
        onSuccess: () => {
          if (post) {
            toast.success(
              intl.formatMessage(
                { id: "calendar.scheduledToast", defaultMessage: "Scheduled for {time}" },
                { time: scheduledToastFormatter.format(new Date(post.scheduledFor)) },
              ),
            );
          }
        },
        onError: (error) => {
          toast.error(
            intl.formatMessage(
              {
                id: "calendar.approvalFailed",
                defaultMessage: "Could not approve post: {message}",
              },
              { message: error.message },
            ),
          );
        },
        onSettled: () =>
          setApprovingIds((prev) => {
            const n = new Set(prev);
            n.delete(postId);
            return n;
          }),
      },
    );
  };

  const rejectPost = (brandId: string, postId: string) => {
    if (selectedPostId === postId) {
      setSelectedPostId(null);
    }

    setRejectingIds((prev) => new Set(prev).add(postId));
    rejectMutation.mutate(
      { brandId, scheduledPostId: postId },
      {
        onSettled: () =>
          setRejectingIds((prev) => {
            const n = new Set(prev);
            n.delete(postId);
            return n;
          }),
      },
    );
  };

  const publishPostNow = (brandId: string, post: CalendarPostLike) => {
    setPublishingIds((current) => new Set(current).add(post.id));

    const callbacks = {
      onSuccess: () => {
        void utils.calendar.list.invalidate();
        toast.success(
          intl.formatMessage({
            id: "approvals.publishNowStarted",
            defaultMessage: "Instagram publishing started.",
          }),
        );
      },
      onError: (error: { message: string }) => {
        toast.error(
          intl.formatMessage(
            {
              id: "approvals.publishNowFailed",
              defaultMessage: "Could not publish to Instagram: {message}",
            },
            { message: error.message },
          ),
        );
      },
      onSettled: () => {
        setPublishingIds((current) => {
          const next = new Set(current);
          next.delete(post.id);
          return next;
        });
      },
    };

    if (post.owner.type === "campaign") {
      publishCampaignMutation.mutate(
        {
          brandId,
          campaignId: post.owner.id,
          scheduledPostId: post.id,
        },
        callbacks,
      );
      return;
    }

    publishStandaloneMutation.mutate(
      {
        brandId,
        batchId: post.owner.id,
        scheduledPostId: post.id,
      },
      callbacks,
    );
  };

  const toggleCollapsed = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div className="min-w-0 py-5">
      <div className="mb-6 px-5 sm:px-8">
        <PageIntro
          title={
            <FormattedMessage id="approvals.reviewContentTitle" defaultMessage="Review Content" />
          }
        />
      </div>

      {showPublishingSetupBanner ? (
        <div className="mx-5 mb-6 flex flex-col gap-3 rounded-xl border border-warning-border bg-warning-surface px-4 py-3 text-sm text-warning sm:mx-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-card text-warning shadow-sm">
              <img src="/instagram.svg" alt="" className="h-4 w-4" aria-hidden={true} />
            </span>
            <p className="min-w-0 font-semibold">
              <FormattedMessage
                id="approvals.publishingSetupIncomplete"
                defaultMessage="Publishing setup incomplete - connect Instagram to schedule and publish content."
              />
            </p>
          </div>
          <Button
            asChild
            variant="outline"
            size="sm"
            className="h-8 shrink-0 rounded-lg border-warning-border bg-card px-4 text-xs font-extrabold text-foreground shadow-sm hover:bg-card/90"
          >
            <Link to={buildBrandPath(brand.id, "/integrations")}>
              <FormattedMessage
                id="approvals.connectInstagram"
                defaultMessage="Connect Instagram"
              />
            </Link>
          </Button>
        </div>
      ) : null}

      {campaignsQuery.isPending ? (
        <section className="mb-6 space-y-3 px-5 sm:px-8">
          <div className="h-4 w-28 animate-pulse rounded-md bg-muted/60" />
          <div className="h-16 animate-pulse rounded-xl bg-muted/40" />
        </section>
      ) : campaignsQuery.error ? (
        <div className="mx-5 mb-6 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive sm:mx-8">
          <FormattedMessage
            id="approvals.campaignsLoadFailed"
            defaultMessage="Failed to load campaigns: {message}"
            values={{ message: campaignsQuery.error.message }}
          />
        </div>
      ) : pendingCampaigns.length > 0 ? (
        <section className="mb-6 space-y-3 px-5 sm:px-8">
          <h2 className="text-xs font-extrabold tracking-[0.18em] text-foreground uppercase">
            <FormattedMessage id="approvals.newCampaigns" defaultMessage="New campaigns" />
          </h2>
          <div className="space-y-3">
            {pendingCampaigns.map((campaign) => (
              <Link
                key={campaign.id}
                to={buildBrandPath(brand.id, `/campaigns/${campaign.id}`)}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card px-4 py-4 shadow-soft transition-colors hover:border-input sm:flex-row sm:items-center"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-extrabold text-foreground">{campaign.name}</p>
                </div>
                <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                  <Badge
                    shape="pill"
                    variant="outline"
                    className="border-warning-border bg-peach px-2 py-0.5 text-[10px] font-extrabold text-warning"
                  >
                    <FormattedMessage id="approvals.needsReview" defaultMessage="Needs review" />
                  </Badge>
                  <span className="inline-flex h-8 items-center gap-2 rounded-full bg-brand px-4 text-xs font-extrabold text-brand-foreground">
                    <FormattedMessage
                      id="approvals.reviewCampaign"
                      defaultMessage="Review campaign"
                    />
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden={true} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <div className="mb-4 flex flex-wrap items-end gap-x-3 gap-y-1 px-5 sm:px-8">
        <div>
          <h2 className="text-xs font-extrabold tracking-[0.18em] text-foreground uppercase">
            <FormattedMessage id="approvals.contentHeading" defaultMessage="Content" />
          </h2>
          <p className="text-xs font-medium text-muted-foreground">
            {view === "campaign" ? (
              <FormattedMessage
                id="approvals.contentDescription"
                defaultMessage="Generated assets from approved campaigns."
              />
            ) : (
              <FormattedMessage
                id="approvals.calendarDescription"
                defaultMessage="Posts to review or publish across the next 7 days."
              />
            )}
          </p>
        </div>
        <div
          role="tablist"
          aria-label={intl.formatMessage({
            id: "approvals.viewSwitcher",
            defaultMessage: "Approval views",
          })}
          className="ml-auto inline-flex rounded-xl border border-border bg-muted/40 p-1"
        >
          <button
            id="approvals-campaign-tab"
            type="button"
            role="tab"
            aria-selected={view === "campaign"}
            aria-controls="approvals-campaign-view"
            onClick={() => setView("campaign")}
            className={cn(
              "rounded-lg px-3 py-2 text-xs font-extrabold transition-colors",
              view === "campaign"
                ? "bg-card text-foreground shadow-soft"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <FormattedMessage id="approvals.campaignView" defaultMessage="Campaign view" />
          </button>
          <button
            id="approvals-calendar-tab"
            type="button"
            role="tab"
            aria-selected={view === "calendar"}
            aria-controls="approvals-calendar-view"
            onClick={() => setView("calendar")}
            className={cn(
              "rounded-lg px-3 py-2 text-xs font-extrabold transition-colors",
              view === "calendar"
                ? "bg-card text-foreground shadow-soft"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <FormattedMessage id="approvals.calendarView" defaultMessage="Calendar view" />
          </button>
        </div>
      </div>

      {view === "campaign" ? (
        <section
          id="approvals-campaign-view"
          role="tabpanel"
          aria-labelledby="approvals-campaign-tab"
          className="mx-auto max-w-[80rem] px-5 sm:px-8"
        >
          {postsQuery.isPending ? (
            <div className="space-y-10">
              {Array.from({ length: 2 }).map((groupPlaceholder, groupIndex) => (
                <div key={groupIndex} className="space-y-4">
                  <Skeleton className="h-7 w-64 rounded-lg" />
                  <MasonryGrid>
                    {Array.from({ length: 5 }).map((postPlaceholder, postIndex) => (
                      <Skeleton
                        key={`${groupPlaceholder}-${postPlaceholder}-${postIndex}`}
                        className="aspect-4/5 rounded-2xl"
                      />
                    ))}
                  </MasonryGrid>
                </div>
              ))}
            </div>
          ) : postsQuery.error ? (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-5 py-4 text-sm text-destructive">
              <FormattedMessage
                id="approvals.loadFailed"
                defaultMessage="Failed to load: {message}"
                values={{ message: postsQuery.error.message }}
              />
            </div>
          ) : reviewGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed py-28 text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
                <Check className="h-6 w-6 text-emerald-500" />
              </div>
              <p className="font-semibold text-foreground">
                <FormattedMessage id="approvals.allCaughtUp" defaultMessage="All caught up" />
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                <FormattedMessage
                  id="approvals.noneNext60Days"
                  defaultMessage="No posts need approval right now."
                />
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {reviewGroups.map((group) => {
                const isCollapsed = collapsed.has(group.key);
                const ownerPath =
                  group.type === "campaign"
                    ? buildBrandPath(brand.id, `/campaigns/${group.id}`)
                    : null;
                const dateRange = formatReviewPostGroupDateRange(
                  [...group.posts, ...group.generatingPosts],
                  dateRangeFormatter,
                );

                return (
                  <section key={group.key}>
                    <div className="mb-4 flex items-center gap-3 border-t border-border pt-4">
                      <button
                        type="button"
                        onClick={() => toggleCollapsed(group.key)}
                        className="flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {isCollapsed ? (
                          <ChevronRight className="h-4 w-4 shrink-0" />
                        ) : (
                          <ChevronDown className="h-4 w-4 shrink-0" />
                        )}
                      </button>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          {ownerPath ? (
                            <Link
                              to={ownerPath}
                              className="text-sm font-extrabold tracking-[0.08em] text-foreground uppercase transition-colors hover:text-foreground/80"
                            >
                              {group.title}
                            </Link>
                          ) : (
                            <h3 className="text-sm font-extrabold tracking-[0.08em] text-foreground uppercase">
                              {group.title}
                            </h3>
                          )}
                          <span className="text-xs font-medium text-muted-foreground">
                            {group.description}
                          </span>
                        </div>
                      </div>
                      {dateRange ? (
                        <span className="ml-auto shrink-0 text-xs font-medium text-muted-foreground">
                          {dateRange}
                        </span>
                      ) : null}
                    </div>

                    {!isCollapsed && (
                      <MasonryGrid>
                        {group.posts.map((post) => (
                          <ApprovalPostCard
                            key={post.id}
                            post={post}
                            brandId={brand.id}
                            profileLabel={profileLabel}
                            logoAssets={logoAssets}
                            onOpen={() => setSelectedPostId(post.id)}
                            approving={approvingIds.has(post.id)}
                            rejecting={rejectingIds.has(post.id)}
                            publishing={publishingIds.has(post.id)}
                            onApprove={() => approvePost(brand.id, post.id)}
                            onReject={() => rejectPost(brand.id, post.id)}
                            onPublishNow={() => publishPostNow(brand.id, post)}
                          />
                        ))}
                        {Array.from({ length: group.generatingPosts.length }).map(
                          (_, placeholderIndex) => (
                            <ApprovalGeneratingPostCard
                              key={`generating-${group.key}-${placeholderIndex}`}
                            />
                          ),
                        )}
                      </MasonryGrid>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </section>
      ) : (
        <section
          id="approvals-calendar-view"
          role="tabpanel"
          aria-labelledby="approvals-calendar-tab"
          className="space-y-4 px-4"
        >
          <div className="mx-5 flex flex-col gap-4 rounded-2xl border border-border bg-card px-4 py-4 shadow-soft sm:mx-8 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-1 text-sm font-semibold">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setCalendarWindowStart((current) => addCalendarDays(current, -7))}
                aria-label={intl.formatMessage({
                  id: "calendar.previousRange",
                  defaultMessage: "Previous 7 days",
                })}
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              </Button>
              <div className="min-w-32 text-center text-sm font-semibold text-foreground">
                {calendarTitleFormatter.format(calendarWindowStart)} -{" "}
                {calendarTitleFormatter.format(calendarWindowEnd)}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCalendarWindowStart(getCalendarWindowStart(new Date()))}
              >
                <CalendarCheck className="h-4 w-4" aria-hidden="true" />
                <FormattedMessage id="calendar.today" defaultMessage="Today" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setCalendarWindowStart((current) => addCalendarDays(current, 7))}
                aria-label={intl.formatMessage({
                  id: "calendar.nextRange",
                  defaultMessage: "Next 7 days",
                })}
              >
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span className={cn(calendarPendingCount > 0 && "font-medium text-foreground")}>
                <FormattedMessage
                  id="approvals.calendarPendingCount"
                  defaultMessage="{count} need approval"
                  values={{ count: calendarPendingCount }}
                />
              </span>
            </div>
          </div>

          <CalendarPostGrid
            groupedDays={groupedCalendarDays}
            profileLabel={profileLabel}
            logoAssets={logoAssets}
            loading={calendarPostsQuery.isPending}
            errorMessage={calendarPostsQuery.error?.message ?? null}
            emptyFutureLabel={intl.formatMessage({
              id: "approvals.calendarNoPosts",
              defaultMessage: "No posts to review or publish",
            })}
            emptyPastLabel={intl.formatMessage({
              id: "approvals.calendarNoPosts",
              defaultMessage: "No posts to review or publish",
            })}
            onOpenPost={(post) => setSelectedPostId(post.id)}
          />
        </section>
      )}

      <ScheduledPostPreviewOverlay
        post={selectedPost}
        brandId={brand.id}
        profileLabel={profileLabel}
        logoAssets={logoAssets}
        backLabel={intl.formatMessage({
          id: "common.back",
          defaultMessage: "Back",
        })}
        hasPrevious={Boolean(previousPost)}
        hasNext={Boolean(nextPost)}
        approving={selectedPost ? approvingIds.has(selectedPost.id) : false}
        rejecting={selectedPost ? rejectingIds.has(selectedPost.id) : false}
        publishing={selectedPost ? publishingIds.has(selectedPost.id) : false}
        onClose={() => setSelectedPostId(null)}
        onPrevious={previousPost ? () => setSelectedPostId(previousPost.id) : undefined}
        onNext={nextPost ? () => setSelectedPostId(nextPost.id) : undefined}
        onApprove={selectedPost ? () => approvePost(brand.id, selectedPost.id) : undefined}
        onReject={selectedPost ? () => rejectPost(brand.id, selectedPost.id) : undefined}
        onPublishNow={selectedPost ? () => publishPostNow(brand.id, selectedPost) : undefined}
      />
    </div>
  );
}

function ApprovalGeneratingPostCard() {
  return (
    <article className="relative overflow-hidden rounded-[1.4rem] border border-brand/45 bg-[linear-gradient(180deg,var(--card),rgba(200,255,0,0.08))] shadow-soft ring-2 ring-brand/15">
      <span
        className="pointer-events-none absolute inset-x-[-20%] top-0 z-10 h-px bg-[linear-gradient(90deg,transparent,rgba(200,255,0,0.9),transparent)] bg-[length:220%_100%] motion-safe:animate-[shimmer_2.4s_ease-in-out_infinite]"
        aria-hidden="true"
      />
      <GeneratedPostVisualSpinner />
      <div className="space-y-3 p-4">
        <Skeleton className="h-3 w-32" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-20 rounded-xl" />
          <Skeleton className="ml-auto h-8 w-24 rounded-xl" />
        </div>
      </div>
    </article>
  );
}
